// ============================================================================
//  91311 (CHATSWORTH) MARKET DATA  —  SINGLE SOURCE OF TRUTH
// ============================================================================
//
//  HOW TO UPDATE (do this monthly, or whenever you pull a fresh MLS report):
//   1. Open your MLS market/area report for ZIP 91311.
//   2. Update the `value` (and `trend`) strings below to match.
//   3. Update `LAST_UPDATED` to today's date and confirm `SOURCE`.
//   4. Save — the page re-renders automatically. You never touch the layout.
//
//  Only edit the text inside quotes. Do NOT change the `key`/`icon` fields
//  unless you intend to change which icon shows.
//  Allowed `icon` values: 'price' | 'sqft' | 'clock' | 'home'
//                         | 'people' | 'money' | 'transit' | 'sun'
// ============================================================================

/** Date these figures were last pulled from the MLS. Format freely, e.g. "June 2026". */
export const LAST_UPDATED = 'June 2026'

/** Where the numbers come from — shown to visitors for credibility. */
export const SOURCE = 'CRMLS / Local MLS Area Report — ZIP 91311'

export interface RealEstateStat {
  label: string
  value: string
  trend: string
  icon: 'price' | 'sqft' | 'clock' | 'home'
  description: string
}

export interface NeighborhoodItem {
  label: string
  value: string
}

export interface NeighborhoodSection {
  category: string
  icon: 'people' | 'money' | 'transit' | 'sun'
  items: NeighborhoodItem[]
}

// ----------------------------------------------------------------------------
//  REAL ESTATE MARKET SNAPSHOT  (the four headline MLS metrics)
//  Pull these straight from your MLS market statistics for 91311.
// ----------------------------------------------------------------------------
export const realEstateStats: RealEstateStat[] = [
  {
    label: 'Median Sale Price',
    value: '$1.05M',
    trend: 'Up 4.2% YoY',
    icon: 'price',
    description: 'Strong, consistent equity growth in the NW Valley',
  },
  {
    label: 'Price Per Sq Ft',
    value: '$520',
    trend: 'Value',
    icon: 'sqft',
    description: 'Excellent baseline for renovations and additions',
  },
  {
    label: 'Avg. Days on Market',
    value: '38',
    trend: 'Fast',
    icon: 'clock',
    description: 'Updated homes move significantly faster',
  },
  {
    label: 'List-to-Sale Ratio',
    value: '99.1%',
    trend: 'Competitive',
    icon: 'home',
    description: 'Sellers are getting their asking price',
  },
]

// ----------------------------------------------------------------------------
//  NEIGHBORHOOD PROFILE  (broader community context)
//  Census / municipal figures — refresh less often than the MLS metrics.
// ----------------------------------------------------------------------------
export const neighborhoodStats: NeighborhoodSection[] = [
  {
    category: 'Demographics',
    icon: 'people',
    items: [
      { label: 'Population', value: '~37,500' },
      { label: 'Median Age', value: '42 Years' },
      { label: 'Owner Occupied', value: '68%' },
    ],
  },
  {
    category: 'Economy',
    icon: 'money',
    items: [
      { label: 'Median Income', value: '$108,500' },
      { label: 'Job Growth', value: 'Positive' },
      { label: 'Major Industries', value: 'Tech, Aerospace, Healthcare' },
    ],
  },
  {
    category: 'Lifestyle & Transit',
    icon: 'transit',
    items: [
      { label: 'Avg Commute', value: '34 Mins' },
      { label: 'Public Transit', value: 'Metrolink Station' },
      { label: 'Freeway Access', value: 'Hwy 118 & 27' },
    ],
  },
  {
    category: 'Environment',
    icon: 'sun',
    items: [
      { label: 'Sunny Days/Yr', value: '286 Days' },
      { label: 'Parks & Trails', value: '15+ Local Parks' },
      { label: 'Notable Nature', value: 'Stoney Point' },
    ],
  },
]
